import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Save, Sparkles, CheckCheck, Loader2, X, Package, Plus, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { listMasterProducts, adoptMasterWithPrices, createProduct, formatMXN } from "@/lib/api";

/**
 * Empty-inventory onboarding. Shopkeeper types the product name (e.g. "COCA COLA"),
 * sees every presentation from the master catalog, types a price for each, and
 * hits GUARDAR. FINALICE MI INVENTARIO cierra el wizard y regresa a Inventario.
 *
 * Props:
 *  - onDone(created: number): called after each successful GUARDAR so parent reloads.
 *  - onFinish(): user tapped "FINALICE MI INVENTARIO".
 */
export default function InventarioOnboarding({ onDone, onFinish, savedCount = 0 }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [prices, setPrices] = useState({}); // {master_id: string}
  const [stocks, setStocks] = useState({}); // {master_id: string} optional
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Manual add form (for products not in the master catalog)
  const [manualOpen, setManualOpen] = useState(false);
  const [manName, setManName] = useState("");
  const [manPresentation, setManPresentation] = useState("");
  const [manPrice, setManPrice] = useState("");
  const [manStock, setManStock] = useState("");
  const [savingManual, setSavingManual] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Debounced live search on the master catalog
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await listMasterProducts({ q: term, limit: 40 });
        setResults(r.items || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 260);
    return () => clearTimeout(t);
  }, [q]);

  const rowsWithPrice = useMemo(
    () => results.filter((r) => Number(prices[r.id]) > 0),
    [results, prices],
  );

  const save = async () => {
    if (rowsWithPrice.length === 0) {
      toast.error("Escribe un precio en al menos un producto");
      return;
    }
    setSaving(true);
    try {
      const items = rowsWithPrice.map((r) => ({
        master_product_id: r.id,
        sale_price: Number(prices[r.id]),
        stock_quantity: Number(stocks[r.id] || 0),
      }));
      const resp = await adoptMasterWithPrices(items);
      const c = (resp.created || 0) + (resp.updated || 0);
      toast.success(
        `${c} producto${c !== 1 ? "s" : ""} guardado${c !== 1 ? "s" : ""} en tu inventario`,
      );
      // clear the just-saved rows
      const clearedP = { ...prices }; const clearedS = { ...stocks };
      items.forEach((it) => { delete clearedP[it.master_product_id]; delete clearedS[it.master_product_id]; });
      setPrices(clearedP); setStocks(clearedS);
      onDone?.(c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar los precios");
    } finally { setSaving(false); }
  };

  const finalize = () => {
    if (rowsWithPrice.length > 0 && !window.confirm(
      "Aún tienes precios sin guardar. ¿Finalizar de todas formas?",
    )) return;
    onFinish?.();
    nav("/inventario");
  };

  const openManual = (prefill = "") => {
    setManName(prefill);
    setManPresentation("");
    setManPrice("");
    setManStock("");
    setManualOpen(true);
  };

  const saveManual = async () => {
    const n = manName.trim();
    const pres = manPresentation.trim();
    const p = parseFloat(manPrice);
    if (!n) { toast.error("Escribe el nombre del producto"); return; }
    if (!p || p <= 0) { toast.error("Escribe un precio válido"); return; }
    const finalName = pres ? `${n} · ${pres}` : n;
    setSavingManual(true);
    try {
      await createProduct({
        name: finalName,
        category: "Otros",
        sale_price: p,
        cost_price: 0,
        stock_quantity: Number(manStock) || 0,
        min_stock_alert: 0,
        unit: "pieza",
      });
      toast.success(`${finalName} agregado a tu inventario`);
      setManualOpen(false);
      setManName(""); setManPresentation(""); setManPrice(""); setManStock("");
      onDone?.(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar");
    } finally { setSavingManual(false); }
  };

  const fmtPresentation = (m) => {
    const parts = [];
    if (m.variant) parts.push(m.variant);
    if (m.net_content_ml) {
      parts.push(m.net_content_ml >= 1000 ? `${(m.net_content_ml / 1000).toFixed(m.net_content_ml % 1000 === 0 ? 0 : 2)} L` : `${m.net_content_ml} ml`);
    }
    if (m.package_type) parts.push(m.package_type);
    return parts.join(" · ");
  };

  return (
    <div className="w-full" data-testid="inv-onboarding">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#CCFF00]/30 bg-[#CCFF00]/[0.05] px-3 py-1 mb-4">
          <Sparkles size={12} className="text-[#CCFF00]" />
          <span className="font-mono text-[10px] tracking-widest text-[#CCFF00] uppercase">Inventario Inicial</span>
        </div>
        <h2 className="font-display text-3xl md:text-5xl leading-none tracking-tighter mb-3">
          Arma tu inventario en <span className="text-[#CCFF00]">minutos</span>.
          <span className="block text-white/50 text-base md:text-lg font-body tracking-normal mt-2">
            Recomendamos hacerlo en computadora.
          </span>
        </h2>
        <p className="text-white/60 max-w-2xl mx-auto text-sm md:text-base">
          Escribe la marca del producto (ej. <span className="text-white">Coca</span> o
          <span className="text-white"> Bimbo</span>), te muestro TODAS las presentaciones
          de nuestro catálogo. Ponle precio a los que vendas y guarda — con eso ya puedes cobrar.
        </p>
        {savedCount > 0 && (
          <p className="mt-3 font-mono text-[10px] tracking-widest text-[#CCFF00] uppercase">
            ✓ Llevas {savedCount} producto{savedCount !== 1 ? "s" : ""} guardado{savedCount !== 1 ? "s" : ""}
          </p>
        )}
      </div>

      {/* Search */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ej. Coca cola, Pan Bimbo, Marías…"
            className="w-full bg-black/60 border border-white/15 rounded-2xl pl-11 pr-11 py-4 text-lg focus:border-[#CCFF00]/60 focus:outline-none placeholder-white/25"
            data-testid="inv-onb-search"
          />
          {q && (
            <button
              onClick={() => { setQ(""); setResults([]); inputRef.current?.focus(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
              aria-label="Limpiar búsqueda"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <p className="text-white/40 text-xs mt-2 text-center">
          Nuestro catálogo tiene más de 4,000 productos. Escribe al menos 2 letras ·{" "}
          <button
            onClick={() => openManual("")}
            className="text-[#FF5A00] hover:text-white underline underline-offset-2 transition"
            data-testid="inv-onb-manual-link"
          >
            o agregar producto manual
          </button>
        </p>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto">
        {loading && (
          <div className="text-center py-8 text-white/50 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Buscando…
          </div>
        )}
        {!loading && q.trim().length >= 2 && results.length === 0 && (
          <div className="rounded-2xl border border-[#FF5A00]/30 bg-[#FF5A00]/[0.04] p-8 text-center" data-testid="inv-onb-empty">
            <Package size={32} className="mx-auto mb-3 text-[#FF5A00]" />
            <p className="font-mono text-[10px] tracking-widest text-[#FF5A00] uppercase mb-2">Marca o producto no encontrado</p>
            <p className="text-white/70 mb-1">No tenemos <span className="text-white font-semibold">{`"${q}"`}</span> en nuestro catálogo maestro.</p>
            <p className="text-white/50 text-sm mb-5">Agrégalo manualmente con nombre, presentación y precio — se guarda en tu inventario junto con los demás.</p>
            <button
              onClick={() => openManual(q)}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FF5A00] text-white font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition"
              data-testid="inv-onb-add-manual"
            >
              <PlusCircle size={14} /> Agregarlo manualmente
            </button>
          </div>
        )}
        {!loading && results.length > 0 && (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-[1fr_140px_100px] md:grid-cols-[1fr_180px_120px] gap-2 px-4 py-3 bg-white/[0.04] text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10">
              <span>Producto · Presentación</span>
              <span className="text-right">Precio venta</span>
              <span className="text-right hidden md:block">Stock inicial</span>
              <span className="md:hidden text-right">Stock</span>
            </div>
            <AnimatePresence initial={false}>
              {results.map((m) => {
                const hasPrice = Number(prices[m.id]) > 0;
                return (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className={`grid grid-cols-[1fr_140px_100px] md:grid-cols-[1fr_180px_120px] gap-2 items-center px-4 py-3 border-b border-white/5 last:border-none transition ${
                      hasPrice ? "bg-[#CCFF00]/[0.04]" : "hover:bg-white/[0.02]"
                    }`}
                    data-testid={`inv-onb-row-${m.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate flex items-center gap-1.5">
                        {hasPrice && <CheckCheck size={12} className="text-[#CCFF00] flex-shrink-0" />}
                        {m.brand ? <span className="font-satoshi">{m.brand}</span> : null}
                        {m.brand ? <span className="text-white/40">·</span> : null}
                        <span className="text-white/80">{m.product_name}</span>
                      </p>
                      <p className="font-mono text-[10px] text-white/40 uppercase tracking-widest truncate">
                        {fmtPresentation(m) || (m.unit || "pieza")}
                      </p>
                    </div>
                    <div className="flex items-center bg-black/60 border border-white/10 rounded-lg px-2 py-1.5 focus-within:border-[#CCFF00]/50">
                      <span className="text-white/40 text-sm mr-1">$</span>
                      <input
                        value={prices[m.id] || ""}
                        onChange={(e) => setPrices({ ...prices, [m.id]: e.target.value.replace(/[^0-9.]/g, "") })}
                        placeholder="0.00"
                        inputMode="decimal"
                        className="w-full bg-transparent text-white text-sm focus:outline-none placeholder-white/25"
                        data-testid={`inv-onb-price-${m.id}`}
                      />
                    </div>
                    <input
                      value={stocks[m.id] || ""}
                      onChange={(e) => setStocks({ ...stocks, [m.id]: e.target.value.replace(/[^0-9.]/g, "") })}
                      placeholder="0"
                      inputMode="decimal"
                      className="bg-black/60 border border-white/10 rounded-lg px-2 py-1.5 text-sm focus:border-[#CCFF00]/50 focus:outline-none text-right placeholder-white/25"
                      data-testid={`inv-onb-stock-${m.id}`}
                      title="Cuántas piezas tienes ahorita (opcional)"
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Action bar */}
        {results.length > 0 && (
          <div className="sticky bottom-4 mt-6" data-testid="inv-onb-actions">
            <div className="rounded-2xl border border-white/15 bg-[#0a0a0a]/95 backdrop-blur-xl p-4 shadow-2xl">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-[10px] tracking-widest uppercase text-white/60">
                  <span className="text-[#CCFF00]">{rowsWithPrice.length}</span> con precio ·{" "}
                  <span className="text-white">
                    {formatMXN(rowsWithPrice.reduce((s, r) => s + Number(prices[r.id] || 0) * (Number(stocks[r.id]) || 1), 0))}
                  </span>{" "}
                  <span className="text-white/40">valor inv.</span>
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={save}
                    disabled={saving || rowsWithPrice.length === 0}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-widest hover:bg-white transition disabled:opacity-40"
                    data-testid="inv-onb-save"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Guardar
                  </button>
                  <button
                    onClick={finalize}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-[#FF5A00]/40 bg-[#FF5A00]/10 text-[#FF5A00] font-mono text-xs uppercase tracking-widest hover:bg-[#FF5A00]/20 transition"
                    data-testid="inv-onb-finalize"
                    title="Presiona esto cuando TODOS tus productos ya tengan precio. Podrás seguir editando después."
                  >
                    <CheckCheck size={14} /> Finalice mi inventario
                  </button>
                </div>
              </div>
              <p className="text-white/40 text-[11px] mt-2 leading-relaxed">
                <span className="text-[#CCFF00] font-semibold">Guardar</span>: agrega solo lo que capturaste ahora, puedes seguir buscando más. ·
                <span className="text-[#FF5A00] font-semibold"> Finalice mi inventario</span>: úsalo cuando todos tus productos ya tienen precio. Podrás ajustar cualquier producto luego desde Inventario.
              </p>
            </div>
          </div>
        )}

        {q.trim().length < 2 && (
          <div className="text-center py-8 text-white/40 text-sm">
            <Plus size={20} className="mx-auto mb-2 opacity-40" />
            Empieza escribiendo el nombre de una marca o producto arriba.
          </div>
        )}
      </div>

      {/* Manual add modal — for products NOT in the master catalog */}
      <AnimatePresence>
        {manualOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setManualOpen(false)}
            data-testid="inv-onb-manual-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <PlusCircle size={16} className="text-[#FF5A00]" />
                  <span className="font-mono text-[10px] tracking-widest text-[#FF5A00] uppercase">
                    Agregar producto manual
                  </span>
                </div>
                <button onClick={() => setManualOpen(false)} className="text-white/40 hover:text-white" data-testid="inv-onb-manual-close">
                  <X size={18} />
                </button>
              </div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tighter mb-1">
                No está en nuestro catálogo
              </h3>
              <p className="text-white/50 text-sm mb-5">
                Llena los datos y lo agrego directo a tu inventario.
              </p>

              <div className="space-y-3">
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Nombre del producto</label>
                  <input
                    autoFocus
                    value={manName}
                    onChange={(e) => setManName(e.target.value)}
                    placeholder="Ej. Pan de elote casero"
                    className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/25 focus:border-[#FF5A00] focus:outline-none"
                    data-testid="inv-onb-manual-name"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Presentación <span className="text-white/30 lowercase tracking-normal">(opcional)</span></label>
                  <input
                    value={manPresentation}
                    onChange={(e) => setManPresentation(e.target.value)}
                    placeholder="Ej. 500 g / 1 pieza / caja"
                    className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/25 focus:border-[#FF5A00] focus:outline-none"
                    data-testid="inv-onb-manual-presentation"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Precio de venta</label>
                    <div className="mt-1 flex items-center bg-black/50 border border-white/10 rounded-xl px-3 py-3 focus-within:border-[#FF5A00]">
                      <span className="text-white/40 mr-1">$</span>
                      <input
                        value={manPrice}
                        onChange={(e) => setManPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                        placeholder="0.00"
                        inputMode="decimal"
                        className="w-full bg-transparent text-white focus:outline-none placeholder-white/25"
                        data-testid="inv-onb-manual-price"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Stock <span className="text-white/30 lowercase tracking-normal">(opc.)</span></label>
                    <input
                      value={manStock}
                      onChange={(e) => setManStock(e.target.value.replace(/[^0-9.]/g, ""))}
                      placeholder="0"
                      inputMode="decimal"
                      className="w-full mt-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/25 focus:border-[#FF5A00] focus:outline-none"
                      data-testid="inv-onb-manual-stock"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={saveManual}
                disabled={savingManual || !manName.trim() || !manPrice}
                className="mt-6 w-full py-4 rounded-full bg-[#FF5A00] text-white font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="inv-onb-manual-save"
              >
                {savingManual ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />}
                Agregar a mi inventario
              </button>
              <p className="text-white/40 text-[11px] mt-3 text-center">
                Se guarda como categoría "Otros". Podrás editar cualquier dato después desde Inventario.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
